import { assign, fromPromise, setup } from 'xstate';
import { isUncertainListingActionError } from '~/features/market/listing-search/errors';
import { MARKET_ACTION_TIMEOUT_MS, MARKET_READ_TIMEOUT_MS } from '~/features/market/requestPolicy';
import type { JobResponse } from '~/types/hlx/jobResponse';
import { withRequestDeadline } from '~/utils/requestDeadline';
import { getData, postData } from '~/utils/service';

export type ApplicationSource = { id: number; jobTitle: string };
const isAppliedResponse = (response: JobResponse, listingId: number) =>
  Number.isSafeInteger(response.id) &&
  response.id > 0 &&
  response.listingType === 'JOB' &&
  response.jobListingId === listingId &&
  !response.deletedFlag &&
  ['APPLIED', 'NEGOTIATING', 'PENDING_ACCEPTANCE', 'ACCEPTED'].includes(response.status);

export const jobApplicationMachine = setup({
  types: {
    context: {} as {
      source: ApplicationSource | null;
      open: boolean;
      uncertain: boolean;
      error: string | null;
      result: JobResponse | null;
    },
    events: {} as
      | { type: 'OPEN'; source: ApplicationSource }
      | { type: 'CLOSE' }
      | { type: 'SEND' }
      | { type: 'RETRY' }
      | { type: 'REFRESH' }
      | { type: 'DISCARD_RETRY' },
  },
  actors: {
    apply: fromPromise(
      ({ input, signal }: { input: ApplicationSource | null; signal: AbortSignal }) =>
        withRequestDeadline(
          async (requestSignal) => {
            if (!input) throw new Error('No application selected.');
            const response = await postData<JobResponse>('jobResponses', {
              data: { listingType: 'JOB', jobListingId: input.id, status: 'APPLIED' },
              signal: requestSignal,
            });
            if (!response || !isAppliedResponse(response, input.id)) {
              throw new Error('Application acknowledgement could not be verified.');
            }
            return response;
          },
          MARKET_ACTION_TIMEOUT_MS,
          signal
        )
    ),
    recover: fromPromise(
      ({ input, signal }: { input: ApplicationSource | null; signal: AbortSignal }) =>
        withRequestDeadline(
          async (requestSignal) => {
            if (!input) throw new Error('No application selected.');
            const listings = await getData<{ id: number; jobResponses?: JobResponse[] }[]>(
              'jobListingMarketplaceSearch',
              {
                params: { listingId: String(input.id), page: '1', pageSize: '1' },
                signal: requestSignal,
              }
            );
            return (
              listings
                .find((listing) => listing.id === input.id)
                ?.jobResponses?.find((response) => isAppliedResponse(response, input.id)) ?? null
            );
          },
          MARKET_READ_TIMEOUT_MS,
          signal
        )
    ),
  },
}).createMachine({
  id: 'jobApplication',
  initial: 'ready',
  context: { source: null, open: false, uncertain: false, error: null, result: null },
  on: {
    CLOSE: { actions: assign({ open: false }) },
  },
  states: {
    ready: {
      on: {
        OPEN: {
          actions: assign({
            source: ({ event }) => ({ ...event.source }),
            open: true,
            error: null,
            result: null,
          }),
        },
        SEND: {
          guard: ({ context }) => context.open && context.source !== null,
          target: 'sending',
        },
      },
    },
    sending: {
      entry: assign({ error: null, result: null }),
      on: { CLOSE: {}, OPEN: {} },
      invoke: {
        src: 'apply',
        input: ({ context }) => context.source,
        onDone: {
          target: 'ready',
          actions: assign({
            result: ({ event }) => event.output,
            open: false,
            uncertain: false,
          }),
        },
        onError: {
          target: 'recovering',
          actions: assign({
            uncertain: ({ context, event }) =>
              context.uncertain || isUncertainListingActionError(event.error),
            error:
              'Application not confirmed. Checking the current response before another action.',
          }),
        },
      },
    },
    recovering: {
      on: { CLOSE: {}, OPEN: {} },
      invoke: {
        src: 'recover',
        input: ({ context }) => context.source,
        onDone: [
          {
            guard: ({ event }) => event.output !== null,
            target: 'ready',
            actions: assign({
              result: ({ event }) => event.output,
              open: false,
              uncertain: false,
              error: null,
            }),
          },
          { guard: ({ context }) => context.uncertain, target: 'uncertain' },
          {
            target: 'ready',
            actions: assign({
              error: 'Application was not submitted. Review the listing and try again.',
            }),
          },
        ],
        onError: 'uncertain',
      },
    },
    uncertain: {
      entry: assign({
        uncertain: true,
        error:
          'We could not confirm whether your application was submitted. No retry happens automatically.',
      }),
      on: {
        OPEN: {
          guard: ({ context, event }) => context.source?.id === event.source.id,
          actions: assign({ open: true }),
        },
        RETRY: { guard: ({ context }) => context.open, target: 'sending' },
        REFRESH: 'recovering',
        DISCARD_RETRY: {
          target: 'ready',
          actions: assign({
            source: null,
            uncertain: false,
            open: false,
            error: null,
            result: null,
          }),
        },
      },
    },
  },
});
